import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);
  const relatedMovies = movies.filter((m) => m.isHot).slice(0, 4);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">文章不存在或已被删除</p>
            <Link to="/" className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryNames: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    anime: '动漫',
    variety: '综艺',
    documentary: '纪录片',
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '成全影视',
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-4">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: '影视资讯', path: '/article/article-1' },
                  { name: article.title },
                ]}
              />

              <article>
                <header className="mb-6">
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="text-xs text-secondary-700 bg-secondary-100 rounded px-2 py-0.5 whitespace-nowrap">
                      {categoryNames[article.category] || article.category}
                    </span>
                    {article.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-foreground-500 bg-background-100 rounded px-2 py-0.5 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-3 mt-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                      {article.publishDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                      {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                    </span>
                  </div>
                </header>

                <div className="relative overflow-hidden rounded-lg aspect-[16/9] mb-8 bg-background-200">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </article>

              <div className="mt-10 pt-8 border-t border-background-200">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  相关推荐
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 mb-10" data-product-shop>
                  {relatedMovies.map((movie) => (
                    <MovieCard key={movie.id} {...movie} />
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-8 border-t border-background-200">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} {...a} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}